import React, { useState, useEffect, useCallback, useRef } from 'react';
import { collection, getDocs, query, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import { FaArrowRight, FaBook, FaCalendarAlt, FaClipboardList, FaBell, FaTasks } from 'react-icons/fa';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/logo.png';
import UserManual from './UserManual';
import ProjectsModal from '../EmployeeManagement/ProjectsModal';
import MyTasksModal from './MyTasksModal';
import WelcomeModal from './WelcomeModal';
import { auth } from '../../config/firebaseConfig';
import { shouldShowWelcome, markWelcomeSeen } from '../../utils/firstLoginWelcome';
import styles from './Dashboard.module.css'; // Import CSS module correctly

const Dashboard = () => {
  const [showManual, setShowManual] = useState(false);
  const [showProjects, setShowProjects] = useState(false);
  const [showMyTasks, setShowMyTasks] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  const { employeeData, employeeId } = useUserProfile();
  const navigate = useNavigate();
  // Karşılama kontrolünün her render'da tekrarlanmasını önler
  const welcomeCheckedRef = useRef(false);

  const fetchUnseenCount = useCallback(async () => {
    if (!employeeId) return;
    try {
      const tasksRef = collection(db, 'Tasks');
      const snapshot = await getDocs(tasksRef);
      const count = snapshot.docs.reduce((acc, d) => {
        const task = d.data();
        const isMine =
          task.assigneeId === employeeId ||
          (task.assignees && task.assignees.some(a => a.id === employeeId));
        if (!isMine) return acc;
        if (task.status === 'completed' || task.status === 'cancelled') return acc;
        const seen = Array.isArray(task.viewedBy) && task.viewedBy.includes(employeeId);
        return seen ? acc : acc + 1;
      }, 0);
      setUnseenCount(count);
    } catch (err) {
      console.error('Error fetching unseen tasks:', err);
    }
  }, [employeeId]);

  // Özgeçmiş güncelleme görevlerini kontrol et ve oluştur (haftalık kontrol)
  const checkAndCreateCVUpdateTasks = useCallback(async () => {
    try {
      // Son kontrol zamanını localStorage'dan al
      const lastCheckKey = 'cvTasksLastCheck';
      const lastCheck = localStorage.getItem(lastCheckKey);
      const now = new Date();
      
      // Eğer son kontrol 7 gün içinde yapıldıysa, tekrar kontrol etme
      if (lastCheck) {
        const lastCheckDate = new Date(lastCheck);
        const daysSinceLastCheck = (now - lastCheckDate) / (1000 * 60 * 60 * 24);
        if (daysSinceLastCheck < 7) {
          console.log(`Özgeçmiş görevleri ${daysSinceLastCheck.toFixed(1)} gün önce kontrol edildi, tekrar kontrol gerekmiyor`);
          return;
        }
      }

      console.log('Özgeçmiş güncelleme görevleri kontrol ediliyor...');
      
      const today = new Date();
      const threeMonthsAgo = new Date(today);
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

      // Tüm çalışanları getir
      const employeesRef = collection(db, 'Employees');
      const employeesSnapshot = await getDocs(employeesRef);
      const allEmployees = employeesSnapshot.docs
        .filter(doc => {
          const data = doc.data();
          // Sadece aktif çalışanlar için görev oluştur
          return data.Active === 1 || data.Active === '1' || data.state === 'aktif';
        })
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

      console.log(`${allEmployees.length} aktif çalışan bulundu`);

      let createdCount = 0;

      for (const employee of allEmployees) {
        const employeeName = `${employee.eName || ''} ${employee.eSurname || ''}`.trim();
        
        // Bu çalışan için özgeçmiş güncelleme görevi var mı kontrol et
        const tasksRef = collection(db, 'Tasks');
        const tasksSnapshot = await getDocs(tasksRef);
        
        // Bu çalışana ait özgeçmiş güncelleme görevlerini bul
        const existingCVTasks = tasksSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(task => 
            task.isCVUpdateTask === true &&
            task.assigneeId === employee.id &&
            task.status !== 'completed' &&
            task.status !== 'cancelled'
          );

        // Eğer aktif bir özgeçmiş güncelleme görevi yoksa, yeni oluştur
        if (existingCVTasks.length === 0) {
          // Son tamamlanan özgeçmiş güncelleme görevini kontrol et
          const completedCVTasks = tasksSnapshot.docs
            .map(doc => ({ id: doc.id, ...doc.data() }))
            .filter(task => 
              task.isCVUpdateTask === true &&
              task.assigneeId === employee.id &&
              task.status === 'completed'
            )
            .sort((a, b) => {
              const aDate = a.completedAt?.toDate?.() || new Date(0);
              const bDate = b.completedAt?.toDate?.() || new Date(0);
              return bDate - aDate;
            });

          let shouldCreateTask = false;

          if (completedCVTasks.length === 0) {
            // Hiç özgeçmiş güncelleme görevi tamamlanmamış, yeni görev oluştur
            shouldCreateTask = true;
          } else {
            // Son tamamlanan görev 3 aydan eski mi kontrol et
            const lastCompletedTask = completedCVTasks[0];
            const lastCompletedDate = lastCompletedTask.completedAt?.toDate?.() || new Date(0);
            
            if (lastCompletedDate < threeMonthsAgo) {
              shouldCreateTask = true;
            }
          }

          if (shouldCreateTask) {
            // Yeni özgeçmiş güncelleme görevi oluştur
            const dueDate = new Date(today);
            dueDate.setDate(dueDate.getDate() + 7); // 1 hafta içinde tamamlanmalı

            const newTask = {
              title: 'Özgeçmiş Güncelleme',
              description: `Lütfen özgeçmişinizi güncelleyiniz. Özgeçmişinizde son 3 aydaki değişiklikleri (yeni projeler, eğitimler, sertifikalar vb.) eklemeyi unutmayınız.`,
              priority: 'medium',
              status: 'pending',
              assigneeId: employee.id,
              assigneeName: employeeName,
              assignees: [{
                id: employee.id,
                name: employeeName,
                email: employee.eMail || ''
              }],
              dueDate: Timestamp.fromDate(dueDate),
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
              isCVUpdateTask: true, // Özgeçmiş güncelleme görevi olduğunu işaretle
              autoCreated: true, // Otomatik oluşturulduğunu işaretle
              createdBy: 'system' // Sistem tarafından oluşturuldu
            };

            await addDoc(collection(db, 'Tasks'), newTask);
            createdCount++;
            console.log(`✅ ${employeeName} için özgeçmiş güncelleme görevi oluşturuldu`);
          }
        }
      }

      // Son kontrol zamanını kaydet
      localStorage.setItem(lastCheckKey, now.toISOString());
      
      if (createdCount > 0) {
        console.log(`✅ ${createdCount} yeni özgeçmiş güncelleme görevi oluşturuldu`);
      } else {
        console.log('✓ Tüm çalışanların özgeçmiş güncelleme görevleri güncel');
      }
    } catch (error) {
      console.error('Özgeçmiş güncelleme görevleri oluşturulurken hata:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnseenCount();
    // Dashboard yüklendiğinde özgeçmiş güncelleme görevlerini kontrol et
    checkAndCreateCVUpdateTasks();
  }, [fetchUnseenCount, checkAndCreateCVUpdateTasks]);

  // Sisteme ilk kez giriş yapan çalışanlara karşılama mesajı göster.
  // Kontrol yalnızca bir kez çalışır; detaylar için utils/firstLoginWelcome.js
  useEffect(() => {
    if (!employeeId || welcomeCheckedRef.current) return;
    welcomeCheckedRef.current = true;

    // NOT: Burada "unmount" temizliği KULLANILMAZ. React.StrictMode geliştirme
    // ortamında effect'leri iki kez çalıştırır; temizlik bayrağı ilk çalıştırmada
    // sonucu iptal eder, ref ise ikinci çalıştırmayı engellerdi ve mesaj hiç
    // açılmazdı. React 18'de setState'in geç çalışması zararsızdır.
    shouldShowWelcome(employeeId).then((isNewcomer) => {
      if (!isNewcomer) return;
      // Gösterildiği anda işaretle: sayfa yenilense bile tekrar açılmasın.
      markWelcomeSeen(auth.currentUser?.uid);
      setShowWelcome(true);
    });
  }, [employeeId]);

  // Function to get user's name
  const getUserName = () => {
    if (employeeData) {
      // First try to get fullName if available
      if (employeeData.fullName) return employeeData.fullName;
      // Then try firstName and lastName combo
      if (employeeData.eName && employeeData.eSurname) {
        return `${employeeData.eName} ${employeeData.eSurname}`;
      }
      // Then try name
      if (employeeData.name) return employeeData.name;
      // Try the displayName
      if (employeeData.displayName) return employeeData.displayName;
      // If nothing else, try using email
      if (employeeData.eMail) {
        // Use part before @ in email
        return employeeData.eMail.split('@')[0];
      }
    }
    // Default if nothing found
    return '';
  };
  
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (showManual || showProjects || showMyTasks || showWelcome) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showManual, showProjects, showMyTasks, showWelcome]);

  const handleShowManual = () => setShowManual(true);
  const handleCloseManual = () => setShowManual(false);
  const handleShowProjects = () => setShowProjects(true);
  const handleCloseProjects = () => setShowProjects(false);
  
  const handleNavigateToCalendar = () => {
    navigate('/profile', { state: { activeTab: 'takvim' } });
  };

  const handleNavigateToNotifications = () => {
    navigate('/profile', { state: { activeTab: 'bildirimler' } });
  };

  const handleCloseWelcome = () => setShowWelcome(false);
  const handleWelcomeOpenManual = () => {
    setShowWelcome(false);
    setShowManual(true);
  };

  const handleOpenMyTasks = () => setShowMyTasks(true);
  const handleCloseMyTasks = () => {
    setShowMyTasks(false);
    fetchUnseenCount();
  };
  
  const userName = getUserName();

  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.welcomeWrapper}>
        <div className={styles.logoContainer}>
          <img 
            src={logo}
            alt="Şirket Logosu"
            className={styles.companyLogo}
          />
        </div>
        
        <div className={styles.welcomeCard}>
          <div className={styles.welcomeCardBody}>
            <h1 className={styles.welcomeTitle}>
              Hoş Geldiniz{userName ? `, ${userName}` : ''}
            </h1>
            <p className={styles.welcomeSubtitle}>
              Şirket portalına hoş geldiniz. Buradan işlemlerinizi gerçekleştirebilir ve bilgilerinize erişebilirsiniz.
            </p>

            {/* Yapılacaklar Listesi - Prominent Top Banner */}
            <div
              className={`${styles.todoBannerTop} ${unseenCount > 0 ? styles.todoBannerUnseen : ''}`}
              onClick={handleOpenMyTasks}
            >
              <div className={styles.todoBannerTopIcon}>
                <FaTasks />
                {unseenCount > 0 && (
                  <span className={styles.unseenBadge}>{unseenCount}</span>
                )}
              </div>
              <div className={styles.todoBannerTopContent}>
                <div className={styles.todoBannerTopHeader}>
                  <h3>Yapılacaklar Listesi</h3>
                  {unseenCount > 0 && (
                    <span className={styles.newPill}>
                      <span className={styles.newPillDot} />
                      {unseenCount} Yeni
                    </span>
                  )}
                </div>
                <p>
                  {unseenCount > 0
                    ? `Size atanmış ${unseenCount} yeni göreviniz var. Hemen göz atın.`
                    : 'Size atanan görevleri görüntüleyin ve takip edin.'}
                </p>
              </div>
              <div className={styles.todoBannerTopAction}>
                Görüntüle <FaArrowRight className={styles.arrowIcon} />
              </div>
            </div>

            <div className={styles.quickLinks}>
              <div className={styles.quickLinksRow}>
                <div className={styles.quickLinkItem}>
                  <div className={styles.quickLinkCard}>
                    <div className={styles.quickLinkIcon}>
                      <FaClipboardList />
                    </div>
                    <h5>Takvimim</h5>
                    <p>Görevlerinizi görüntüleyin ve yönetin.</p>
                    <button 
                      className={styles.quickLinkButton}
                      onClick={handleNavigateToCalendar}
                    >
                      Görüntüle <FaArrowRight className={styles.arrowIcon} />
                    </button>
                  </div>
                </div>

                <div className={styles.quickLinkItem}>
                  <div className={styles.quickLinkCard}>
                    <div className={styles.quickLinkIcon}>
                      <FaCalendarAlt />
                    </div>
                    <h5>Projelerim</h5>
                    <p>Projelerinizi takip edin ve güncelleyin.</p>
                    <button 
                      className={styles.quickLinkButton}
                      onClick={handleShowProjects}
                    >
                      Görüntüle <FaArrowRight className={styles.arrowIcon} />
                    </button>
                  </div>
                </div>

                <div className={styles.quickLinkItem}>
                  <div className={styles.quickLinkCard}>
                    <div className={styles.quickLinkIcon}>
                      <FaBell />
                    </div>
                    <h5>Duyurular</h5>
                    <p>Şirket duyurularını takip edin.</p>
                    <button 
                      className={styles.quickLinkButton}
                      onClick={handleNavigateToNotifications}
                    >
                      Görüntüle <FaArrowRight className={styles.arrowIcon} />
                    </button>
                  </div>
                </div>

              </div>
            </div>

            <div className={styles.manualButtonContainer}>
              <button 
                className={styles.manualButton}
                onClick={handleShowManual}
              >
                <FaBook className={styles.bookIcon} /> Kullanım Kılavuzu
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* User Manual Modal */}
      <UserManual isOpen={showManual} onClose={handleCloseManual} />

      {/* Projects Modal */}
      {showProjects && employeeData && (
        <ProjectsModal
          employee={employeeData}
          onClose={handleCloseProjects}
        />
      )}

      {/* My Tasks Modal */}
      <MyTasksModal isOpen={showMyTasks} onClose={handleCloseMyTasks} />

      {/* İlk giriş karşılama mesajı (yalnızca yeni çalışanlara, bir kez) */}
      <WelcomeModal
        isOpen={showWelcome}
        userName={userName}
        onClose={handleCloseWelcome}
        onOpenManual={handleWelcomeOpenManual}
      />

    </div>
  );
};

export default Dashboard;
